import React, { createContext, useMemo, useCallback } from 'react';

export const TranslationsContext = createContext<TranslationsContextProps>({} as TranslationsContextProps);

interface TranslationsContextProps {
  t?:any,
  lang?:any,
  changeLanguage?:any,
  locales?:any,
}

type ContextProps = {
  children?:any,
  dictionary?:any,
  lang?:any,
  locales?:any,
  changeLanguage?: (lang: string) => void,
}

const TranslationsContextProvider: React.FC<ContextProps> = (props) => {
  const t = useMemo(() => {
    return (key: string, params?: Record<string, string | number>) => {
      const interpolate = (value: any) => {
        if (typeof value !== 'string' || !params) return value;
        return Object.entries(params).reduce((acc, [paramKey, paramValue]) => {
          const doubleBraces = new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g');
          const singleBraces = new RegExp(`{\\s*${paramKey}\\s*}`, 'g');
          return acc.replace(doubleBraces, String(paramValue)).replace(singleBraces, String(paramValue));
        }, value as string);
      };
      const resolveKey = (source: any) => {
        return key.split('.').reduce((obj, part) => {
          return obj ? obj[part] : undefined;
        }, source);
      };
      // Try to find key in global, client, or partner sections
      const sections = ['global', 'client', 'partner'];
      
      for (const section of sections) {
        if (props?.dictionary?.[section]) {
          const value = resolveKey(props.dictionary[section]);
          if (value !== undefined) return interpolate(value);
        }
      }
      
      // Fallback: try in root dictionary (backward compatibility)
      const value = resolveKey(props?.dictionary);
      const resolved = value !== undefined ? interpolate(value) : undefined;
      return resolved ?? key;
    };
  }, [props?.dictionary]);
  const changeLanguage = useCallback(
    (locale: string) => {
      if (props.changeLanguage) {
        props.changeLanguage(locale);
      }
    },
    [props]
  );
  return (
    <TranslationsContext.Provider 
      value={{
        t,
        lang:props?.lang,
        changeLanguage,
        locales:props.locales
      }}
    >
      {props?.children}
    </TranslationsContext.Provider>
  );
};

const useTranslations = () => {
  const context = React.useContext(TranslationsContext);
  if (context === undefined) {
    throw new Error('useTranslations must be used within a TranslationsProvider');
  }
  return context as TranslationsContextProps;
};

export { TranslationsContextProvider, useTranslations };
